import { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Container,
    IconButton,
    Paper,
    Stack,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import { apiFetch } from '../lib/api';
import { PageHeader } from '../components/PageHeader';

/**
 * Program Settings.
 *
 * Small lists the rest of the app picks from — now just the two ATO Single
 * Touch Payroll codes. The legacy module was a parent controller plus five
 * children, five tables, and every one of those tables had zero rows. Staff,
 * task and job file types were dropped because nothing read them.
 *
 * Each list says what it drives, because a settings screen whose effect is
 * invisible is how you end up with lists nobody dares touch.
 */

interface Setting {
    readonly id: number;
    readonly kind: string;
    readonly value: string;
    readonly label: string | null;
    readonly isActive: boolean;
}

interface Kind {
    readonly kind: string;
    readonly label: string;
    readonly whereUsed: string;
    readonly codeLike: boolean;
}

export function ProgramSettings(): React.JSX.Element {
    const [kinds, setKinds] = useState<readonly Kind[]>([]);
    const [settings, setSettings] = useState<readonly Setting[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [nonce, setNonce] = useState(0);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        apiFetch<{ kinds: readonly Kind[]; settings: readonly Setting[] }>('/program-settings')
            .then((r) => {
                if (!cancelled) {
                    setKinds(r.kinds);
                    setSettings(r.settings);
                    setError(null);
                }
            })
            .catch((err: unknown) => {
                if (!cancelled) {
                    setError(err instanceof Error ? err.message : 'Could not load settings');
                }
            })
            .finally(() => {
                if (!cancelled) {
                    setLoading(false);
                }
            });
        return () => {
            cancelled = true;
        };
    }, [nonce]);

    const reload = useCallback(() => {
        setNonce((n) => n + 1);
    }, []);

    const byKind = useMemo(() => {
        const m = new Map<string, Setting[]>();
        for (const s of settings) {
            const list = m.get(s.kind) ?? [];
            list.push(s);
            m.set(s.kind, list);
        }
        return m;
    }, [settings]);

    return (
        <Container maxWidth="xl" sx={{ py: 4 }}>
            <PageHeader title="Program Settings" subtitle="Lists the rest of the app picks from" />

            {error !== null ? (
                <Alert
                    severity="error"
                    sx={{ mb: 2 }}
                    action={<Button onClick={reload}>Retry</Button>}
                    onClose={() => {
                        setError(null);
                    }}
                >
                    {error}
                </Alert>
            ) : null}

            {loading && kinds.length === 0 ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                    <CircularProgress size={24} />
                </Box>
            ) : null}

            <Stack gap={4}>
                {kinds.map((k) => (
                    <SettingList
                        key={k.kind}
                        kind={k}
                        entries={byKind.get(k.kind) ?? []}
                        onChanged={reload}
                        onError={setError}
                    />
                ))}
            </Stack>
        </Container>
    );
}

// ─── One list ───────────────────────────────────────────────────────────────

interface SettingListProps {
    readonly kind: Kind;
    readonly entries: readonly Setting[];
    readonly onChanged: () => void;
    readonly onError: (message: string) => void;
}

function SettingList({ kind, entries, onChanged, onError }: SettingListProps): React.JSX.Element {
    const [adding, setAdding] = useState(false);
    const [draftValue, setDraftValue] = useState('');
    const [draftLabel, setDraftLabel] = useState('');
    const [editingId, setEditingId] = useState<number | null>(null);
    const [busy, setBusy] = useState(false);
    const columns = kind.codeLike ? 4 : 3;

    function reset(): void {
        setAdding(false);
        setEditingId(null);
        setDraftValue('');
        setDraftLabel('');
    }

    function save(): void {
        setBusy(true);
        const body = { kind: kind.kind, value: draftValue.trim(), label: draftLabel.trim() };
        const path = editingId === null ? '/program-settings' : `/program-settings/${String(editingId)}`;
        apiFetch<unknown>(path, {
            method: editingId === null ? 'POST' : 'PATCH',
            body: JSON.stringify(body),
        })
            .then(() => {
                reset();
                onChanged();
            })
            .catch((err: unknown) => {
                onError(err instanceof Error ? err.message : 'Could not save');
            })
            .finally(() => {
                setBusy(false);
            });
    }

    function remove(id: number): void {
        setBusy(true);
        apiFetch<unknown>(`/program-settings/${String(id)}`, { method: 'DELETE' })
            .then(onChanged)
            .catch((err: unknown) => {
                onError(err instanceof Error ? err.message : 'Could not remove');
            })
            .finally(() => {
                setBusy(false);
            });
    }

    function toggle(s: Setting, isActive: boolean): void {
        apiFetch<unknown>(`/program-settings/${String(s.id)}`, {
            method: 'PATCH',
            body: JSON.stringify({ isActive }),
        })
            .then(onChanged)
            .catch((err: unknown) => {
                onError(err instanceof Error ? err.message : 'Could not save');
            });
    }

    const editRow = (key: string): React.JSX.Element => (
        <EditRow
            key={key}
            codeLike={kind.codeLike}
            value={draftValue}
            label={draftLabel}
            onValue={setDraftValue}
            onLabel={setDraftLabel}
            onSave={save}
            onCancel={reset}
            busy={busy}
        />
    );

    return (
        <Box>
            <Paper sx={{ p: 2, mb: 1.5 }}>
                <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={2}
                    alignItems={{ xs: 'stretch', sm: 'center' }}
                    justifyContent="space-between"
                >
                    <Box>
                        <Typography variant="h6" fontWeight={700}>
                            {kind.label}
                            <Typography component="span" sx={{ ml: 1, color: 'text.secondary', fontWeight: 400 }}>
                                ({entries.length})
                            </Typography>
                        </Typography>
                        {/* What this list actually drives. The legacy screens gave no
                            clue, which is part of why none of them was ever used. */}
                        <Typography variant="body2" color="text.secondary">
                            {kind.whereUsed}
                        </Typography>
                    </Box>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        disabled={adding}
                        onClick={() => {
                            reset();
                            setAdding(true);
                        }}
                        sx={{ flex: '0 0 auto' }}
                    >
                        Add
                    </Button>
                </Stack>
            </Paper>

            <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
                <Table sx={{ minWidth: 560 }}>
                    <TableHead>
                        <TableRow>
                            {/* The code is what goes to Xero, so it gets its own column. */}
                            {kind.codeLike ? <TableCell sx={{ width: 240 }}>Code</TableCell> : null}
                            <TableCell>{kind.codeLike ? 'Label' : 'Name'}</TableCell>
                            <TableCell sx={{ width: 140 }}>Status</TableCell>
                            <TableCell align="right" sx={{ width: 120 }}>
                                Actions
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {entries.length === 0 && !adding ? (
                            <TableRow>
                                <TableCell colSpan={columns} align="center" sx={{ py: 5 }}>
                                    <Typography color="text.secondary">Nothing in this list yet.</Typography>
                                </TableCell>
                            </TableRow>
                        ) : null}

                        {entries.map((s, idx) =>
                            editingId === s.id ? (
                                editRow(String(s.id))
                            ) : (
                                <TableRow
                                    key={s.id}
                                    hover
                                    sx={{
                                        bgcolor: idx % 2 !== 0 ? 'action.hover' : 'transparent',
                                        opacity: s.isActive ? 1 : 0.55,
                                    }}
                                >
                                    {kind.codeLike ? (
                                        <TableCell sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                                            {s.value}
                                        </TableCell>
                                    ) : null}
                                    <TableCell sx={{ fontWeight: kind.codeLike ? 400 : 600 }}>
                                        {kind.codeLike ? (s.label ?? '—') : s.value}
                                    </TableCell>
                                    <TableCell>
                                        <Tooltip title={s.isActive ? 'Offered in pickers' : 'Hidden from pickers'}>
                                            <Stack direction="row" alignItems="center" gap={0.5}>
                                                <Switch
                                                    size="small"
                                                    checked={s.isActive}
                                                    onChange={(e) => {
                                                        toggle(s, e.target.checked);
                                                    }}
                                                />
                                                <Typography variant="body2" color="text.secondary">
                                                    {s.isActive ? 'Active' : 'Hidden'}
                                                </Typography>
                                            </Stack>
                                        </Tooltip>
                                    </TableCell>
                                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                                        <Tooltip title="Edit">
                                            <IconButton
                                                size="small"
                                                color="primary"
                                                onClick={() => {
                                                    setAdding(false);
                                                    setEditingId(s.id);
                                                    setDraftValue(s.value);
                                                    setDraftLabel(s.label ?? '');
                                                }}
                                            >
                                                <EditIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Delete">
                                            <span>
                                                <IconButton
                                                    size="small"
                                                    color="error"
                                                    disabled={busy}
                                                    onClick={() => {
                                                        remove(s.id);
                                                    }}
                                                >
                                                    <DeleteOutlineIcon fontSize="small" />
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            ),
                        )}

                        {adding ? editRow('new') : null}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
}

interface EditRowProps {
    readonly codeLike: boolean;
    readonly value: string;
    readonly label: string;
    readonly onValue: (v: string) => void;
    readonly onLabel: (v: string) => void;
    readonly onSave: () => void;
    readonly onCancel: () => void;
    readonly busy: boolean;
}

function EditRow({
    codeLike,
    value,
    label,
    onValue,
    onLabel,
    onSave,
    onCancel,
    busy,
}: EditRowProps): React.JSX.Element {
    const keys = (e: React.KeyboardEvent): void => {
        if (e.key === 'Enter' && value.trim() !== '') {
            onSave();
        }
        if (e.key === 'Escape') {
            onCancel();
        }
    };
    const valueField = (
        <TextField
            size="small"
            fullWidth
            autoFocus
            placeholder={codeLike ? 'CODE' : 'Name'}
            value={value}
            onChange={(e) => {
                onValue(e.target.value);
            }}
            onKeyDown={keys}
            // Spread rather than passed as undefined: exactOptionalPropertyTypes
            // rejects an explicit undefined for an optional prop.
            {...(codeLike ? { slotProps: { htmlInput: { style: { fontFamily: 'monospace' } } } } : {})}
        />
    );
    return (
        <TableRow>
            {codeLike ? <TableCell>{valueField}</TableCell> : null}
            <TableCell>
                {codeLike ? (
                    <TextField
                        size="small"
                        fullWidth
                        placeholder="Readable label"
                        value={label}
                        onChange={(e) => {
                            onLabel(e.target.value);
                        }}
                        onKeyDown={keys}
                    />
                ) : (
                    valueField
                )}
            </TableCell>
            <TableCell />
            <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                <Tooltip title="Save">
                    <span>
                        <IconButton
                            size="small"
                            color="primary"
                            disabled={busy || value.trim() === ''}
                            onClick={onSave}
                        >
                            <CheckIcon fontSize="small" />
                        </IconButton>
                    </span>
                </Tooltip>
                <Tooltip title="Cancel">
                    <IconButton size="small" onClick={onCancel}>
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
            </TableCell>
        </TableRow>
    );
}
